import { datadogRum } from '@datadog/browser-rum';
import { createJSONStorage } from 'jotai/utils';
import { z } from 'zod';

export const PlanBuildTimerSourceSchema = z.enum(['blank', 'template_copy']);

export const PlanBuildTimerEntrySchema = z.object({
  startedAt: z.string(),
  source: PlanBuildTimerSourceSchema,
});

export const PlanBuildTimerMapSchema = z.record(
  z.string(),
  PlanBuildTimerEntrySchema,
);

export type PlanBuildTimerSource = z.infer<typeof PlanBuildTimerSourceSchema>;
export type PlanBuildTimerEntry = z.infer<typeof PlanBuildTimerEntrySchema>;
export type PlanBuildTimerMap = z.infer<typeof PlanBuildTimerMapSchema>;

export const DEFAULT_PLAN_BUILD_TIMER_MAP = PlanBuildTimerMapSchema.parse({});

const parsePlanBuildTimerSource = (
  value: unknown,
): PlanBuildTimerSource | null => {
  if (value === 'blank' || value === 'template_copy') {
    return value;
  }
  return null;
};

const PersistedPlanBuildTimerEntrySchema = z
  .object({
    startedAt: z.unknown().optional(),
    source: z.unknown().optional(),
  })
  .loose()
  .transform(data => {
    const source = parsePlanBuildTimerSource(data.source);
    const startedAt =
      typeof data.startedAt === 'string' &&
      data.startedAt.length > 0 &&
      Number.isFinite(Date.parse(data.startedAt))
        ? data.startedAt
        : null;

    if (!source || !startedAt) {
      return null;
    }

    return PlanBuildTimerEntrySchema.parse({ startedAt, source });
  });

const PersistedPlanBuildTimerMapSchema = z
  .record(z.string(), z.unknown())
  .transform(data => {
    const entries = Object.entries(data).flatMap(([planId, value]) => {
      const parsed = PersistedPlanBuildTimerEntrySchema.safeParse(value);
      return parsed.success && parsed.data
        ? [[planId, parsed.data] as const]
        : [];
    });

    return PlanBuildTimerMapSchema.parse(Object.fromEntries(entries));
  });

const parsePlanBuildTimerMap = (parsed: unknown): PlanBuildTimerMap => {
  const result = PersistedPlanBuildTimerMapSchema.safeParse(parsed);
  return result.success ? result.data : DEFAULT_PLAN_BUILD_TIMER_MAP;
};

const baseStorage = createJSONStorage<PlanBuildTimerMap>(() => localStorage);

export const planBuildTimerStorage: typeof baseStorage = {
  ...baseStorage,
  getItem: (key, initialValue) => {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return initialValue;
    }

    try {
      const parsed: unknown = JSON.parse(raw);
      return parsePlanBuildTimerMap(parsed);
    } catch (error) {
      localStorage.removeItem(key);
      datadogRum.addError(
        error instanceof Error
          ? error
          : new Error('Failed to parse plan build timer storage'),
      );
      return initialValue;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      datadogRum.addError(
        error instanceof Error
          ? error
          : new Error('Failed to persist plan build timer storage'),
        {
          entryCount: Object.keys(value).length,
        },
      );
    }
  },
  removeItem: key => {
    localStorage.removeItem(key);
  },
};
